import { Component, Prop, Vue } from "vue-property-decorator";
import { Icon, Drawer, Button, Table, Select, Checkbox, Row, Col, Tooltip, Form, Divider, Modal } from "ant-design-vue";
import ContentExposureItemAction from "@/components/content-exposure/content-exposure-item-action/content-exposure-item-action.vue";
import {
	ContentExposureRelatedItemCategory,
	ContentExposureRelatedItemType,
	ContentExposureRelatedItemTypeName,
	ContentGroupEnum,
	ContentTypeEnum,
	LanguageSkill,
	ParentType,
	SpecialDesignation
} from "@/util/enum";
import { ContentSimpleModel, ResourceService } from "@/services/resource";
import { deepClone, stripHtml } from "@/util/func";
import { ActionItem, MessageHelper, NotificationType, fmtMsg } from "commonui";
import { CommonLocale, ContentExposureLocale } from "@/locales/localeid";
import { orderBy, uniq, uniqBy } from "lodash";
import RichTextSingleLineEditor from "@/components/rich-text-single-line-editor/rich-text-single-line-editor.vue";
import RichTextPreviewer from "@/components/rich-text-previewer/rich-text-previewer.vue";
import { v4 } from "uuid";
import { getModule } from "vuex-module-decorators";
import TitleListState from "@/modules/title-list";
import Loading from "@/components/loading/loading.vue";
import { ContentService } from "@/services/content";
import { TypeResponseModel } from "@/services/category-manager";
import { ContentExposureService } from "@/services/content-exposure";
export interface ContentExposureRelatedItemProps {
	isEdit?: boolean;
	isAnyEdit?: boolean;
	initValues?: any[];
	versionGroupId?: string;
	vcsLevelId?: string;
}

@Component({
	components: {
		Icon,
		Drawer,
		Button,
		Table,
		ContentExposureItemAction,
		Select,
		SelectOption: Select.Option,
		Checkbox,
		CheckboxGroup: Checkbox.Group,
		Row,
		Col,
		RichTextSingleLineEditor,
		RichTextPreviewer,
		Form,
		FormItem: Form.Item,
		Tooltip,
		Divider,
		ActionItem,
		Loading
	}
})
export default class ContentExposureRelatedItem extends Vue {
	@Prop({ default: false }) isEdit!: boolean;
	@Prop({ default: false }) isAnyEdit!: boolean;
	@Prop({ default: () => [] }) initValues!: any[];
	@Prop() versionGroupId!: string;
	@Prop() vcsLevelId!: string;
	@Prop() contentId!: string;
	@Prop({ default: "" }) contentTitle?: string;
	@Prop({ default: false }) showTooltip!: boolean; // to all disable tooltip permanently
	@Prop({ default: false }) isREP?: boolean;
	@Prop() parentTypeId?: ParentType;
	@Prop() currentExposureId?: string;

	rules = [
		{ required: true, message: fmtMsg(CommonLocale.CommonFieldRequired, { name: "custom text" }) },
		{ max: 200, message: fmtMsg(ContentExposureLocale.ContentExposureRelatedItemsFieldLength) },
		{ validator: this.validateInputCustomText }
	];
	editRules = [
		{ required: true, message: fmtMsg(CommonLocale.CommonFieldRequired, { name: "custom text" }) },
		{ max: 200, message: fmtMsg(ContentExposureLocale.ContentExposureRelatedItemsFieldLength) },
		{ validator: this.validateEditInputCustomText }
	];
	form;
	editForm;
	vcsLevels: any[] = [];
	selectedVCSLevelId: string = "";
	selectedComponents: any[] = [];
	otherSelectedComponents: any[] = [];
	inputCustomText: any = "";
	visible = false;
	relatedItems: any[] = [];
	isAdded: boolean = false;
	currentType: ContentExposureRelatedItemCategory = ContentExposureRelatedItemCategory.Component;
	chapters: any[] = [];
	selectedChapters: any[] = [];
	loading: boolean = false;
	private titleListModule = getModule(TitleListState);
	public lessonRelatedSubCategories: TypeResponseModel[] = [];
	public lessonRelatedComponents: any[] = [];
	public otherLessonRelatedComponents: any[] = [];
	public selectedSubCategory: number = -1;
	public specialDesignationId?: number = 1;

	public columns: any = [
		{
			title: fmtMsg(ContentExposureLocale.ContentExposureRelatedItemsSequence),
			dataIndex: "sequence",
			key: "sequence",
			width: "10%"
		},
		{
			title: fmtMsg(ContentExposureLocale.ContentExposureRelatedItemsType),
			dataIndex: "typeName",
			key: "typeName",
			width: "30%"
		},
		{
			title: fmtMsg(ContentExposureLocale.ContentExposureRelatedItemsItem),
			dataIndex: "title",
			key: "title",
			width: "40%"
		},
		{
			title: fmtMsg(ContentExposureLocale.ContentExposureRelatedItemsUnit),
			dataIndex: "unitName",
			key: "unitName",
			width: "10%"
		},
		{
			title: "",
			dataIndex: "action",
			key: "action",
			align: "right",
			width: "10%"
		}
	];

	tooltipVisible = false;
	locale = CommonLocale;

	$refs!: {
		content: HTMLDivElement;
	};
	editItemId?: null | number = null;

	get isParent() {
		return (
			(this.titleListModule.isParent && this.titleListModule.editeContentSelectedChapterId === "all") ||
			this.parentTypeId === ParentType.Parent
		);
	}

	get drawerTitle() {
		return this.formatMessage(ContentExposureLocale.ContentExposureRelatedItems);
	}
	get contentExposureRelatedItemCategory() {
		return ContentExposureRelatedItemCategory;
	}
	get contentExposureLocale() {
		return ContentExposureLocale;
	}
	get commonLocale() {
		return CommonLocale;
	}
	get contentExposureRelatedItemTypeName() {
		return ContentExposureRelatedItemTypeName;
	}
	get contentExposureRelatedItemType() {
		return ContentExposureRelatedItemType;
	}
	get contentTypeEnum() {
		return ContentTypeEnum;
	}
	get specialDesignationEnum() {
		return SpecialDesignation;
	}
	get defaultChapters() {
		const chapters = this.chapters;
		const colCount = 2;
		const rowCount = Math.ceil(chapters.length / colCount);
		return new Array(colCount).fill(0).map((_, index) => chapters.slice(rowCount * index, rowCount * (index + 1)));
	}
	get defaultComponents() {
		const components = this.lessonRelatedComponents;
		const colCount = 3;
		const rowCount = Math.ceil(components.length / colCount);
		return new Array(colCount)
			.fill(0)
			.map((_, index) => components.slice(rowCount * index, rowCount * (index + 1)));
	}
	get defaultOtherComponents() {
		const components = this.otherLessonRelatedComponents;
		const colCount = 3;
		const rowCount = Math.ceil(components.length / colCount);
		return new Array(colCount)
			.fill(0)
			.map((_, index) => components.slice(rowCount * index, rowCount * (index + 1)));
	}
	get relatedItemChapters() {
		return this.sortContents(ContentExposureRelatedItemType.Chapter)
			.map((d) => d.title)
			.join(", ");
	}
	get relatedItemCustomTexts() {
		return this.initValues.filter((d) => d.type === ContentExposureRelatedItemType.CustomText);
	}
	get relatedItemComponents() {
		return this.groupComponents();
	}
	get isComponentAllInCurrentVCS() {
		return this.relatedItemComponents.every((c) => c.vcsLevelId === this.currentVCSLevelId);
	}
	get currentVCSLevelId() {
		return this.selectedVCSLevelId || this.vcsLevelId;
	}
	get disabledAdd() {
		if (this.editItemId) {
			return true;
		}
		return false;
	}
	get checkedComponents() {
		return this.selectedComponents.flatMap((s) => s.items).flatMap((s) => s.items);
	}
	get currentCategoryName() {
		return this.lessonRelatedSubCategories.find((c) => c.id === this.selectedSubCategory)?.name;
	}
	get showUnitDropDown() {
		return this.vcsLevels.length > 1 && this.currentType === ContentExposureRelatedItemCategory.Component;
	}

	vpcComponentText(groupItem: { unitGroup: { items: { title: string; typeSequence?: number | null; }[]; }[]; }) {
		const items = groupItem.unitGroup.flatMap((s) => s.items);
		return this.sortItems(ContentExposureRelatedItemType.Component, items, ["typeSequence", "title"])
			.map((c) => c.title)
			.join(", ");
	}
	async created() {
		if (!this.form) {
			this.form = this.$form.createForm(this, { name: "customText" });
		}
		if (!this.editForm) {
			this.editForm = this.$form.createForm(this, { name: "editCustomText" });
		}
	}

	mounted() {
		const parent = this.$refs.content.parentNode as HTMLTableCellElement;
		if (parent) {
			parent.addEventListener("dblclick", this.onDoubleClick);
			parent.addEventListener("mouseenter", this.onMouseEnter);
			parent.addEventListener("mouseleave", this.onMouseLeave);
		}
	}

	beforeDestroy() {
		const parent = this.$refs.content.parentNode as HTMLTableCellElement;
		if (parent) {
			parent.removeEventListener("dblclick", this.onDoubleClick);
			parent.removeEventListener("mouseenter", this.onMouseEnter);
			parent.removeEventListener("mouseleave", this.onMouseLeave);
		}
	}

	onDoubleClick() {
		!this.isEdit && this.$emit("doubleClick");
	}

	onMouseEnter() {
		this.tooltipVisible = true;
	}

	onMouseLeave() {
		this.tooltipVisible = false;
	}

	groupComponents() {
		let sortedContents = this.sortContents(
			ContentExposureRelatedItemType.Component,
			["contentType.name", "unit", "title"],
			["asc", "desc", "asc"]
		);

		const singleLetterPhonogramComponents = this.sortItems(
			ContentExposureRelatedItemType.Component,
			sortedContents.filter((c) => c.contentType.specialDesignationId === SpecialDesignation.LetterPhonogramCard),
			["unit", "sequence"],
			["desc", "asc"]
		);

		const phonogramWordCardComponents = this.sortItems(
			ContentExposureRelatedItemType.Component,
			sortedContents.filter((c) => c.contentType.specialDesignationId === SpecialDesignation.PhonogramWordCard),
			["unit", "sequence"],
			["desc", "asc"]
		);

		const vpcComponents = this.sortItems(
			ContentExposureRelatedItemType.Component,
			sortedContents.filter((c) => c.contentType.specialDesignationId === SpecialDesignation.VPC),
			["typeSequence", "title"]
		);

		const singleLetterPhonogramStartIndex = sortedContents.findIndex((c) => c.contentType.specialDesignationId === SpecialDesignation.LetterPhonogramCard);
		const phonogramWordCardStartIndex = sortedContents.findIndex((c) => c.contentType.specialDesignationId === SpecialDesignation.PhonogramWordCard);
		const vpcStartIndex = sortedContents.findIndex(
			(c) => c.contentType.specialDesignationId === SpecialDesignation.VPC
		);

		sortedContents = [
			...sortedContents.slice(0, singleLetterPhonogramStartIndex),
			...singleLetterPhonogramComponents,
			...sortedContents.slice(singleLetterPhonogramStartIndex + singleLetterPhonogramComponents.length)
		];
		sortedContents = [
			...sortedContents.slice(0, phonogramWordCardStartIndex),
			...phonogramWordCardComponents,
			...sortedContents.slice(phonogramWordCardStartIndex + phonogramWordCardComponents.length)
		];
		sortedContents = [
			...sortedContents.slice(0, vpcStartIndex),
			...vpcComponents,
			...sortedContents.slice(vpcStartIndex + vpcComponents.length)
		];

		const componentGroup = sortedContents.reduce((result, value) => {
			const key = `${value.contentType.specialDesignationId}${value.contentType.id}`;
			let group = result.find((d) => d.key === key);

			if (group) {
				const unitGroup = group.unitGroup.find((u) => u.unitId === value.vcsLevelId);

				if (unitGroup) {
					unitGroup.items.push(value);
				} else {
					group.unitGroup.push({
						unitId: value.vcsLevelId,
						unitName: value.unitName,
						items: [value]
					});
				}
			} else {
				group = {
					key: key,
					specialDesignationId: value.contentType.specialDesignationId,
					contentTypeName: value.contentType.name,
					unitGroup: [
						{
							unitId: value.vcsLevelId,
							unitName: value.unitName,
							items: [value]
						}
					]
				};
				result.push(group);
			}

			return result;
		}, []);

		return componentGroup;
	}
	isInCurrentVCS(phonograms: any[]) {
		return phonograms.length === 1 && phonograms[0].id === this.vcsLevelId;
	}
	async onTypeChange(value) {
		this.currentType = value;
		if (value != ContentExposureRelatedItemCategory.CustomText) {
			const isChapter = value === ContentExposureRelatedItemCategory.Chapters;
			const contents = await ResourceService.getContentExposureRelatedItemContents({
				versionGroupId: this.versionGroupId,
				unitId: this.vcsLevelId,
				contentId: this.contentId,
				isVPC: false,
				isChapter
			});
			this.selectedVCSLevelId = this.vcsLevelId;
			const chapters = this.toRelatedItems(contents.data, "Chapter", ContentExposureRelatedItemTypeName.Chapter);
			if (isChapter) {
				this.chapters = chapters;
				this.selectedChapters = this.getSelectedChapter();
			}
		}
	}

	async onVCSLevelChange(value, currentType) {
		this.selectedVCSLevelId = value;
		this.specialDesignationId = this.lessonRelatedSubCategories.find(s => s.id === this.selectedSubCategory)?.specialDesignationId;
		if (currentType === this.contentExposureRelatedItemCategory.Component) {
			const { lessonRelatedComponents, otherLessonRelatedComponents } = await this.getLessonRelatedComponents({
				versionGroupId: this.versionGroupId,
				subCategoryId: this.selectedSubCategory,
				unitId: +this.currentVCSLevelId,
				contentId: this.specialDesignationId === SpecialDesignation.VPC ? this.contentId : null,
				specialDesignationId: this.specialDesignationId
			});

			this.lessonRelatedComponents = this.toRelatedItems(
				lessonRelatedComponents,
				ContentExposureRelatedItemTypeName.Component,
				null
			);
			this.otherLessonRelatedComponents = this.toRelatedItems(
				otherLessonRelatedComponents,
				ContentExposureRelatedItemTypeName.Component,
				null
			);
		}
	}

	async onSubCategoryChange(value) {
		this.selectedSubCategory = +value;
		this.specialDesignationId = this.lessonRelatedSubCategories.find(s => s.id === this.selectedSubCategory)?.specialDesignationId;
		const { lessonRelatedComponents, otherLessonRelatedComponents } = await this.getLessonRelatedComponents({
			versionGroupId: this.versionGroupId,
			subCategoryId: this.selectedSubCategory,
			unitId: +this.currentVCSLevelId,
			contentId: this.specialDesignationId === SpecialDesignation.VPC ? this.contentId : null,
			specialDesignationId: this.specialDesignationId
		});

		this.lessonRelatedComponents = this.toRelatedItems(
			lessonRelatedComponents,
			ContentExposureRelatedItemTypeName.Component,
			null
		);
		this.otherLessonRelatedComponents = this.toRelatedItems(
			otherLessonRelatedComponents,
			ContentExposureRelatedItemTypeName.Component,
			null
		);
	}

	move(sequence, step) {
		const index = this.relatedItems.findIndex((d) => d.sequence === sequence);
		if ((index === 0 && step < 0) || (this.relatedItems.length - 1 === index && step > 0)) {
			return;
		}
		const item = this.relatedItems[index];
		const nextItem = this.relatedItems[index + step];
		const temp = item.sequence;
		item.sequence = nextItem.sequence;
		nextItem.sequence = temp;
		this.relatedItems = this.relatedItems.sort((pre, next) => pre.sequence - next.sequence);
	}
	onMoveUping({ sequence }) {
		this.move(sequence, -1);
	}
	onMoveDowning({ sequence }) {
		this.move(sequence, 1);
	}
	async onRemoving({ sequence, type, contentId }) {
		let isChapterInContentExposureReaderContent = false;
		if(contentId) {
			isChapterInContentExposureReaderContent = await ContentExposureService.isChapterInContentExposureReaderContent(
				this.currentExposureId as string,
				contentId
			);
		}

		if (isChapterInContentExposureReaderContent) {
			MessageHelper.Message(
				NotificationType.Warning,
				this.formatMessage(ContentExposureLocale.ContentExposureRelatedItemChapterReferenceWarning)
			);
			return;
		} else {
			this.relatedItems.splice(
				this.relatedItems.findIndex((d) => d.sequence === sequence),
				1
			);
			this.relatedItems.forEach((d, index) => (d.sequence = index + 1));
			switch (type) {
				case ContentExposureRelatedItemType.Chapter:
					this.onChapterChange(this.selectedChapters.filter((id) => id !== contentId));
					break;
				default:
					break;
			}

			const values = this.selectedComponents
				.flatMap((s) => s.items)
				.flatMap((s) => s.items)
				.filter((id) => id != contentId);

			this.onComponentChange(values, 1);
		}
	}
	onChapterChange(values) {
		this.selectedChapters = values;
		this.onAddRelatedItems();
	}
	onComponentChange(values, flag?) {
		const subCategoryItem = this.selectedComponents.find((s) => s.subCategoryId === this.selectedSubCategory);

		if (subCategoryItem) {
			const index = this.selectedComponents.findIndex((s) => s.subCategoryId === subCategoryItem.subCategoryId);
			const unitIndex: number = subCategoryItem.items.findIndex((s) => s.unitId === this.currentVCSLevelId);

			if (unitIndex >= 0) {
				this.selectedComponents = [
					...this.selectedComponents.slice(0, index),
					{
						...subCategoryItem,
						items: [
							...subCategoryItem.items.slice(0, unitIndex),
							{
								...subCategoryItem.items[unitIndex],
								items: [...values]
							},
							...subCategoryItem.items.slice(unitIndex + 1)
						]
					},
					...this.selectedComponents.slice(index + 1)
				];
			} else {
				subCategoryItem.items = [
					...subCategoryItem.items,
					{
						unitId: +this.currentVCSLevelId,
						items: [...values]
					}
				];
			}
		} else {
			this.selectedComponents = [
				...this.selectedComponents,
				{
					subCategoryId: this.selectedSubCategory,
					items: [
						{
							unitId: +this.currentVCSLevelId,
							items: [...values]
						}
					]
				}
			];
		}
		if (flag == 1) {
			return;
		} else {
			this.onAddRelatedItems();
		}
	}
	onOtherComponentChange(values) {
		const subCategoryItem = this.otherSelectedComponents.find((s) => s.subCategoryId === this.selectedSubCategory);

		if (subCategoryItem) {
			const index = this.otherSelectedComponents.findIndex(
				(s) => s.subCategoryId === subCategoryItem.subCategoryId
			);
			const unitIndex: number = subCategoryItem.items.findIndex((s) => s.unitId === this.currentVCSLevelId);

			if (unitIndex >= 0) {
				this.otherSelectedComponents = [
					...this.otherSelectedComponents.slice(0, index),
					{
						...subCategoryItem,
						items: [
							...subCategoryItem.items.slice(0, unitIndex),
							{
								...subCategoryItem.items[unitIndex],
								items: [...values]
							},
							...subCategoryItem.items.slice(unitIndex + 1)
						]
					},
					...this.otherSelectedComponents.slice(index + 1)
				];
			} else {
				subCategoryItem.items = [
					...subCategoryItem.items,
					{
						unitId: +this.currentVCSLevelId,
						items: [...values]
					}
				];
			}
		} else {
			this.otherSelectedComponents = [
				...this.otherSelectedComponents,
				{
					subCategoryId: this.selectedSubCategory,
					items: [
						{
							unitId: +this.currentVCSLevelId,
							items: [...values]
						}
					]
				}
			];
		}

		this.onAddRelatedItems();
	}
	onAddRelatedItems() {
		// Do not allow adding of items if any item is being edited.
		if (this.editItemId) {
			return;
		}

		switch (this.currentType) {
			case ContentExposureRelatedItemCategory.CustomText:
				this.form.validateFields((err, values) => {
					if (!err) {
						this.onAddCustomText(values.customText);
						this.form.resetFields();
					}
				});
				break;
			case ContentExposureRelatedItemCategory.Chapters:
				this.onAddChapters();
				break;
			case ContentExposureRelatedItemCategory.Component:
				this.onAddComponents();
				break;
			default:
				break;
		}
	}
	onCancel() {
		this.isAdded = false;
	}
	onAddChapters() {
		this.relatedItems = this.orderRelatedContents(
			this.relatedItems
				.filter((d) => d.type !== ContentExposureRelatedItemType.Chapter)
				.concat(
					this.chapters
						.filter((c) => this.selectedChapters.includes(c.contentId))
						.map((d) => ({ ...d, uid: d.uid || v4() }))
				)
		);
	}
	onAddComponents() {
		const selectedItems = this.selectedComponents.flatMap((s) => s.items).flatMap((s) => s.items);

		this.relatedItems = this.orderRelatedContents(
			this.relatedItems
				.filter(
					(d) =>
						!(d.type == ContentExposureRelatedItemType.Component && d.vcsLevelId == this.currentVCSLevelId)
				)
				.concat(this.relatedItems.filter((r) => selectedItems.includes(r.contentId)))
				.concat(
					this.lessonRelatedComponents
						.filter((v) => selectedItems.includes(v.contentId))
						.map((d) => ({ ...d, uid: d.uid || v4(), typeSequence: d.typeSequence || null }))
				)
				.concat(
					this.otherLessonRelatedComponents
						.filter((v) => selectedItems.includes(v.contentId))
						.map((d) => ({ ...d, uid: d.uid || v4(), typeSequence: d.typeSequence || null }))
				)
		);
	}
	validateInputCustomText(rule, value, callback) {
		if (this.checkCustomTextUniq(value)) {
			callback(fmtMsg(ContentExposureLocale.ContentExposureRelatedItemsFieldExisted));
			return;
		}
		callback();
	}
	validateEditInputCustomText(rule, value, callback) {
		if (this.checkEditCustomTextUniq(value)) {
			callback(fmtMsg(ContentExposureLocale.ContentExposureRelatedItemsFieldExisted));
			return;
		}
		callback();
	}
	transformCustomText(value) {
		if (!value) {
			return value;
		}

		return (stripHtml(value) || "").toLowerCase();
	}
	checkCustomTextUniq(customText) {
		const customTexts = this.relatedItems
			.filter((d) => d.type === ContentExposureRelatedItemType.CustomText)
			.map((d) => this.transformCustomText(d.text))
			.concat([this.transformCustomText(customText)]) as string[];
		return customTexts.length != uniq(customTexts).length;
	}
	checkEditCustomTextUniq(customText) {
		const customTexts = this.relatedItems
			.filter((d) => d.uid !== this.editItemId)
			.filter((d) => d.type === ContentExposureRelatedItemType.CustomText)
			.map((d) => this.transformCustomText(d.text))
			.concat([this.transformCustomText(customText)]) as string[];
		return customTexts.length != uniq(customTexts).length;
	}
	onAddCustomText(customText) {
		this.relatedItems = this.orderRelatedContents(
			this.relatedItems.concat([
				{
					title: customText,
					text: customText,
					typeName: ContentExposureRelatedItemTypeName.CustomText,
					type: ContentExposureRelatedItemType.CustomText,
					uid: v4()
				}
			])
		);
	}
	sortContents(type, props = ["title"], orders = ["asc"]) {
		return this.sortItems(type, this.initValues, props, orders);
	}
	sortItems(type, items: any[], props = ["title"], orders = ["asc"]) {
		return orderBy(
			items.filter((d) => d.type === type),
			props,
			orders as any
		);
	}
	orderRelatedContents(relatedItems: any[]) {
		const sort = (type, props = ["title"], orders = ["asc"]) =>
			uniqBy(this.sortItems(type, relatedItems, props, orders), "contentId");
		let components = sort(
			ContentExposureRelatedItemType.Component,
			["typeName", "unit", "title"],
			["asc", "desc", "asc"]
		);

		const singleLetterPhonogramComponents = this.sortItems(
			ContentExposureRelatedItemType.Component,
			components.filter((c) => c.contentType.specialDesignationId === SpecialDesignation.LetterPhonogramCard),
			["unit", "typeSequence"],
			["desc", "asc"]
		);

		const phonogramWordCardComponents = this.sortItems(
			ContentExposureRelatedItemType.Component,
			components.filter((c) => c.contentType.specialDesignationId === SpecialDesignation.PhonogramWordCard),
			["unit", "typeSequence"],
			["desc", "asc"]
		);

		const vpcComponents = this.sortItems(
			ContentExposureRelatedItemType.Component,
			components.filter((c) => c.contentType.specialDesignationId === SpecialDesignation.VPC),
			["typeSequence", "title"]
		);

		const singleLetterPhonogramStartIndex = components.findIndex((c) => c.contentType.specialDesignationId === SpecialDesignation.LetterPhonogramCard);
		const phonogramWordCardStartIndex = components.findIndex((c) => c.contentType.specialDesignationId === SpecialDesignation.PhonogramWordCard);
		const vpcStartIndex = components.findIndex((c) => c.contentType.specialDesignationId === SpecialDesignation.VPC);
		
		components = [
			...components.slice(0, singleLetterPhonogramStartIndex),
			...singleLetterPhonogramComponents,
			...components.slice(singleLetterPhonogramStartIndex + singleLetterPhonogramComponents.length)
		];
		components = [
			...components.slice(0, phonogramWordCardStartIndex),
			...phonogramWordCardComponents,
			...components.slice(phonogramWordCardStartIndex + phonogramWordCardComponents.length)
		];
		components = [
			...components.slice(0, vpcStartIndex),
			...vpcComponents,
			...components.slice(vpcStartIndex + vpcComponents.length)
		];

		const customTexts = relatedItems.filter((d) => d.type === ContentExposureRelatedItemType.CustomText);
		const chapters = sort(ContentExposureRelatedItemType.Chapter);
		return ([] as any[])
			.concat(components)
			.concat(customTexts)
			.concat(chapters)
			.map((d, index) => ({
				...d,
				sequence: index + 1
			}));
	}
	async showDrawer() {
		this.isAdded = false;
		this.currentType = this.defaultContentExposureRelatedItemCategory;
		this.visible = true;
		this.loading = true;
		this.vcsLevels = await ResourceService.getContentExposureRelatedVCSLevels({
			vcsLevelId: this.vcsLevelId,
			hasLowerVcsLevel: true
		});
		const { getVCSName, getTypeNameByObject } = this.getNameMapper();
		this.relatedItems = this.orderRelatedContents(
			deepClone(this.initValues).map((d) => ({
				...d,
				typeName: getTypeNameByObject(d),
				unitName: getVCSName(d.vcsLevelId),
				uid: d.uid || v4(),
				typeSequence: d.typeSequence
			}))
		);
		this.loading = false;

		if (this.currentType === ContentExposureRelatedItemCategory.Component) {
			if (this.isREP) {
				this.lessonRelatedSubCategories = await this.getLessonRelatedSubCategories(SpecialDesignation.VPC);
			} else {
				this.lessonRelatedSubCategories = await this.getLessonRelatedSubCategories();
			}

			if (this.lessonRelatedSubCategories.length) {
				this.selectedSubCategory = this.lessonRelatedSubCategories[0].id;
				this.specialDesignationId = this.lessonRelatedSubCategories[0].specialDesignationId;
				const { lessonRelatedComponents, otherLessonRelatedComponents } = await this.getLessonRelatedComponents(
					{
						versionGroupId: this.versionGroupId,
						subCategoryId: this.selectedSubCategory,
						unitId: +this.currentVCSLevelId,
						contentId:
							this.specialDesignationId === SpecialDesignation.VPC ? this.contentId : null,
						specialDesignationId: this.specialDesignationId
					}
				);

				this.lessonRelatedComponents = this.toRelatedItems(
					lessonRelatedComponents,
					ContentExposureRelatedItemTypeName.Component,
					null
				);
				this.otherLessonRelatedComponents = this.toRelatedItems(
					otherLessonRelatedComponents,
					ContentExposureRelatedItemTypeName.Component,
					null
				);
			}
		} else if (this.currentType === ContentExposureRelatedItemCategory.Chapters) {
			const contents = await ResourceService.getContentExposureRelatedItemContents({
				versionGroupId: this.versionGroupId,
				unitId: this.vcsLevelId,
				contentId: this.contentId,
				isVPC: false,
				isChapter: true
			});
			this.selectedVCSLevelId = this.vcsLevelId;
			const chapters = this.toRelatedItems(contents.data, "Chapter", ContentExposureRelatedItemTypeName.Chapter);
			this.chapters = chapters;
			this.selectedChapters = this.getSelectedChapter();
		}

		this.selectedComponents = this.getSelectedComponents();
	}
	getSelectedComponents() {
		return this.relatedItems
			.filter((d) => d.type === ContentExposureRelatedItemType.Component)
			.reduce((acc, item) => {
				const subCategoryItem = acc.find((a) => a.subCategoryId === item.contentType.id);

				if (subCategoryItem) {
					const unitItem = subCategoryItem.items.find((s) => s.unitId === item.vcsLevelId);

					if (unitItem) {
						unitItem.items.push(item.contentId);
					} else {
						subCategoryItem.items.push({
							unitId: item.vcsLevelId,
							items: [item.contentId]
						});
					}
				} else {
					acc.push({
						subCategoryId: item.contentType.id,
						items: [
							{
								unitId: item.vcsLevelId,
								items: [item.contentId]
							}
						]
					});
				}

				return acc;
			}, []);
	}
	getSelectedChapter() {
		return this.relatedItems
			.filter((d) => d.type === ContentExposureRelatedItemType.Chapter)
			.map((d) => d.contentId);
	}
	getNameMapper() {
		const getTypeNameBy = (id) =>
		({
			[ContentExposureRelatedItemType.CustomText]: ContentExposureRelatedItemTypeName.CustomText,
			[ContentExposureRelatedItemType.Chapter]: ContentExposureRelatedItemTypeName.Chapter,
			[ContentExposureRelatedItemType.Component]: ContentExposureRelatedItemTypeName.Component
		}[id]);

		return {
			getTypeName: (id) =>
			({
				[SpecialDesignation.VPC]: ContentExposureRelatedItemTypeName.VPC,
				[SpecialDesignation.LetterPhonogramCard]: ContentExposureRelatedItemTypeName.PC,
				[SpecialDesignation.PhonogramWordCard]: ContentExposureRelatedItemTypeName.PWC
			}[id]),
			getTypeNameByObject: (d) => {
				if (d.type === ContentExposureRelatedItemType.Component && d.contentType) {
					return d.contentType.name;
				}

				return getTypeNameBy(d.type);
			},
			getVCSName: (id) => {
				if (!id) {
					return "";
				}
				return this.vcsLevels.find((d) => d.id === id)?.displayName;
			}
		};
	}
	toRelatedItems(contents: ContentSimpleModel[], type: string, typeName: string | null) {
		const { getVCSName, getTypeNameByObject } = this.getNameMapper();
		const cont = contents.map((d) => ({
			contentId: d.id,
			title: d.title,
			typeName: typeName === null ? getTypeNameByObject(d) : typeName,
			type: type && ContentExposureRelatedItemType[ContentExposureRelatedItemTypeName[type]],
			unit: d.unit || this.vcsLevels.find((v) => v.id == d.vcsLevelId)?.unit,
			unitName: getVCSName(d.vcsLevelId),
			vcsLevelId: d.vcsLevelId,
			content: { sequence: d.sequence },
			typeSequence: d.sequence,
			contentType: d.contentType
		}));

		return cont;
	}
	onAddItemClick() {
		if (!this.editItemId) {
			this.isAdded = true;
		}
	}
	onEditItem(record) {
		this.editItemId = record.uid;
	}
	onEditCancel() {
		this.editItemId = null;
	}
	onEditSave(record) {
		this.editForm.validateFields((err, values) => {
			if (!err) {
				const index = this.relatedItems.findIndex((r) => r.uid === record.uid);

				if (index >= 0) {
					this.relatedItems = [
						...this.relatedItems.slice(0, index),
						{
							...this.relatedItems[index],
							title: values.editCustomText,
							text: values.editCustomText
						},
						...this.relatedItems.slice(index + 1)
					];
					this.editForm.resetFields();
					this.editItemId = null;
				}
			}
		});
	}
	onClose() {
		const close = () => {
			this.visible = false;
			this.editItemId = null;
			this.selectedVCSLevelId = "";
		};

		if (this.editItemId) {
			this.getConfirmModal(close);
		} else {
			close();
		}
	}
	onSubmit() {
		const submit = () => {
			this.visible = false;
			this.$emit("submit", deepClone(this.relatedItems));
			this.editItemId = null;
			this.selectedVCSLevelId = "";
		};

		if (this.editItemId) {
			this.getConfirmModal(submit);
		} else {
			submit();
		}
	}
	getConfirmModal(onConfirm) {
		return Modal.confirm({
			title: fmtMsg(ContentExposureLocale.ContentExposureRelatedItemsConfirmCancelMsg),
			okText: fmtMsg(CommonLocale.CommonYesButtonText),
			cancelText: fmtMsg(CommonLocale.CommonCancelButtonText),
			onOk: onConfirm
		});
	}
	formatMessage(id: string, params?: any): string {
		return fmtMsg(id, params);
	}
	async getLessonRelatedSubCategories(specialDesignationId?: number) {
		return await ContentService.getLessonRelatedItemSubCategories(specialDesignationId);
	}
	async getLessonRelatedComponents(params: {
		versionGroupId: string;
		subCategoryId: number;
		unitId: number;
		contentId?: string | null;
		specialDesignationId?: number;
	}) {
		const { data, notMatchedData } = await ContentService.getLessonRelatedItemComponents(params);

		const lessonRelatedComponents = data.map((r) => ({
			...r,
			contentType: this.lessonRelatedSubCategories.find((s) => s.id === this.selectedSubCategory),
			type: ContentExposureRelatedItemType.Component,
			typeName: this.lessonRelatedSubCategories.find((s) => s.id === this.selectedSubCategory)?.name
		}));

		const otherLessonRelatedComponents = notMatchedData.map((r) => ({
			...r,
			contentType: this.lessonRelatedSubCategories.find((s) => s.id === this.selectedSubCategory),
			type: ContentExposureRelatedItemType.Component,
			typeName: this.lessonRelatedSubCategories.find((s) => s.id === this.selectedSubCategory)?.name
		}));

		return { lessonRelatedComponents, otherLessonRelatedComponents };
	}

	get isREPReaderParent() {
		return (
			this.isREP &&
			this.isParent &&
			this.titleListModule.editedContentDetails.languageSkillId === LanguageSkill.Read
		);
	}

	get defaultContentExposureRelatedItemCategory() {
		if (!this.isREPReaderParent) {
			return ContentExposureRelatedItemCategory.Component;
		} else {
			return ContentExposureRelatedItemCategory.Chapters;
		}
	}
}
